
'use client';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';

import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  MapPin,
  Plane,
  ShieldCheck,
  Star,
  Users,
  BriefcaseBusiness,
  Car,
  Route,
  CalendarCheck,
  MessageCircle,
  ChevronDown,
  Building2,
  Compass,
  BookOpenText,
  KeyRound,
} from 'lucide-react';

const WA_NUMBER = '6287786556067';

const createWhatsAppUrl = (vehicle = '') => {
  const message = `Halo NAYVERA INDO TRANSPORT,

Saya ingin menanyakan layanan rental mobil Tangerang.

DATA PEMESANAN

Nama pemesan:
Kendaraan yang diminati: ${vehicle}
Jenis sewa: Dengan sopir / Lepas kunci
Tanggal sewa:
Jam mulai:
Jam selesai:
Lokasi penjemputan:
Tujuan / rute:
Jumlah penumpang:

Mohon informasi mengenai ketersediaan unit, harga, pilihan paket, fasilitas, persyaratan, dan biaya tambahan jika ada.

Terima kasih.`;

  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;
};

const fleet = [
  {
    name: 'Toyota Agya / Daihatsu Ayla',
    category: 'City Car',
    price: 'Rp298.000',
    package: 'Lepas Kunci',
    image: '/assets/images/AYLA DAN AGYA.jpg',
    description:
      'Pilihan ringkas untuk aktivitas harian, perjalanan singkat, dan kebutuhan mobilitas di kawasan Tangerang.',
  },
  {
    name: 'Toyota Avanza',
    category: 'Family MPV',
    price: 'Rp598.000',
    package: 'Lepas Kunci',
    image: '/assets/images/Avanza.jpeg',
    description:
      'Pilihan kendaraan untuk perjalanan keluarga, kunjungan, dan aktivitas bersama rekan kerja.',
  },
  {
    name: 'Toyota Innova Reborn',
    category: 'Family & Business',
    price: 'Rp750.000',
    package: 'Lepas Kunci',
    image: '/assets/images/REBORN.jpeg',
    description:
      'Alternatif untuk perjalanan keluarga, kebutuhan profesional, dan perjalanan antarkota.',
  },
  {
    name: 'Toyota Innova Zenix',
    category: 'Executive MPV',
    price: 'Rp850.000',
    package: 'Lepas Kunci',
    image: '/assets/images/ZENIX G.webp',
    description:
      'MPV dengan desain modern untuk kebutuhan perjalanan keluarga maupun mobilitas profesional.',
  },
  {
    name: 'Toyota Fortuner',
    category: 'Premium SUV',
    price: 'Rp1.700.000',
    package: 'Dengan Sopir',
    image: '/assets/images/Fortuner.jpeg',
    description:
      'Pilihan SUV untuk kebutuhan bisnis, perjalanan keluarga, acara, dan mobilitas dengan karakter premium.',
  },
  {
    name: 'Toyota Hiace Premio',
    category: 'Premium Van',
    price: 'Rp1.850.000',
    package: 'Dengan Sopir',
    image: '/assets/images/Hiace-Premio.jpeg',
    description:
      'Kendaraan untuk perjalanan bersama, wisata, kegiatan perusahaan, dan kebutuhan rombongan.',
  },
];
const services = [
  {
    icon: KeyRound,
    title: 'Sewa Mobil Lepas Kunci',
    description:
      'Untuk pelanggan yang membutuhkan fleksibilitas berkendara sendiri. Ketersediaan unit dan persyaratan mengikuti ketentuan penyewaan.',
  },
  {
    icon: Users,
    title: 'Sewa Mobil dengan Sopir',
    description:
      'Pilihan untuk perjalanan keluarga, kunjungan kerja, kegiatan perusahaan, wisata, maupun kebutuhan transportasi harian.',
  },
  {
    icon: Plane,
    title: 'Antar Jemput Bandara',
    description:
      'Rencanakan perjalanan menuju atau dari Bandara Internasional Soekarno-Hatta dengan jadwal dan titik penjemputan yang disepakati.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Transportasi Bisnis',
    description:
      'Kendaraan untuk meeting, kunjungan klien, perjalanan dinas, dan mobilitas perusahaan di Tangerang dan sekitarnya.',
  },
  {
    icon: Route,
    title: 'Perjalanan Luar Kota',
    description:
      'Konsultasikan kebutuhan perjalanan ke kota lain agar pilihan kendaraan dan rincian biaya dapat disesuaikan.',
  },
  {
    icon: Star,
    title: 'Wisata dan Acara',
    description:
      'Pilihan kendaraan untuk wisata keluarga, gathering, acara pernikahan, kegiatan komunitas, dan kebutuhan event.',
  },
];

const useCases = [
  {
    icon: CalendarCheck,
    title: 'Mobilitas Harian',
    description:
      'Untuk aktivitas sehari-hari, kunjungan, urusan keluarga, dan perjalanan dalam kawasan Tangerang.',
  },
  {
    icon: Building2,
    title: 'Kebutuhan Perusahaan',
    description:
      'Mendukung transportasi tamu, meeting, kunjungan proyek, dan kegiatan bisnis.',
  },
  {
    icon: Plane,
    title: 'Perjalanan Bandara',
    description:
      'Siapkan jadwal penerbangan, jumlah penumpang, bagasi, serta lokasi penjemputan sebelum memesan.',
  },
  {
    icon: Users,
    title: 'Perjalanan Keluarga',
    description:
      'Pilih kendaraan dengan mempertimbangkan jumlah penumpang, barang bawaan, dan lama perjalanan.',
  },
  {
    icon: Compass,
    title: 'Wisata dan Rekreasi',
    description:
      'Tentukan tujuan, jadwal, serta jumlah peserta untuk membantu memilih kendaraan yang sesuai.',
  },
  {
    icon: Route,
    title: 'Perjalanan Antarkota',
    description:
      'Konsultasikan rute dan durasi perjalanan agar layanan yang dipilih sesuai kebutuhan.',
  },
];

const areaGroups = [
  {
    title: 'Kota Tangerang',
    description: 'Kebutuhan perjalanan di pusat kota dan kawasan sekitarnya.',
    places: ['Kota Tangerang', 'Karawaci', 'Ciledug', 'Batuceper', 'Jatiuwung'],
  },
  {
    title: 'Tangerang Selatan',
    description: 'Layanan sesuai titik penjemputan dan ketersediaan kendaraan.',
    places: ['BSD City', 'Serpong', 'Pamulang', 'Ciputat', 'Pondok Aren', 'Bintaro'],
  },
  {
    title: 'Kawasan Bisnis dan Hunian',
    description: 'Konsultasikan kebutuhan kendaraan di kawasan komersial dan perumahan.',
    places: ['Gading Serpong', 'Alam Sutera', 'Lippo Karawaci', 'Summarecon Serpong'],
  },
  {
    title: 'Bandara dan Luar Kota',
    description: 'Perjalanan berdasarkan jadwal, rute, dan kesepakatan layanan.',
    places: ['Bandara Soekarno-Hatta', 'Jakarta', 'Bogor', 'Bekasi', 'Kota tujuan lainnya'],
  },
];

const articles = [
  {
    number: '01',
    category: 'Panduan Penyewaan',
    title: 'Cara Memilih Rental Mobil Tangerang Sesuai Kebutuhan',
    paragraphs: [
      'Sebelum memesan kendaraan, tentukan tujuan perjalanan, jumlah penumpang, lama pemakaian, dan barang bawaan. Informasi ini membantu menentukan jenis mobil yang sesuai tanpa memilih kendaraan yang terlalu besar atau justru kurang memadai.',
      'Periksa pula jenis layanan yang tersedia, ketentuan penggunaan, lokasi penjemputan, serta rincian biaya. Pastikan durasi sewa, kemungkinan biaya tambahan, dan prosedur pengembalian kendaraan telah dipahami sebelum melakukan konfirmasi.',
      'Untuk mempermudah konsultasi dengan NAYVERA, siapkan tanggal penggunaan, rute, dan pilihan kendaraan. Admin dapat membantu memeriksa ketersediaan serta menjelaskan paket yang sesuai dengan kebutuhan perjalanan.',
    ],
  },
  {
    number: '02',
    category: 'Panduan Layanan',
    title: 'Sewa Mobil Lepas Kunci atau dengan Sopir: Apa Bedanya?',
    paragraphs: [
      'Lepas kunci merupakan pilihan bagi pelanggan yang ingin mengemudikan kendaraan sendiri dan memenuhi persyaratan penyewaan. Sebelum memilihnya, tanyakan dokumen yang dibutuhkan, ketentuan penggunaan, area perjalanan yang diizinkan, serta prosedur serah terima kendaraan.',
      'Sewa mobil dengan sopir dapat menjadi alternatif bagi pelanggan yang ingin fokus pada agenda perjalanan. Layanan ini dapat dipertimbangkan untuk meeting, kunjungan bisnis, perjalanan keluarga, atau perjalanan menuju bandara.',
      'Pilihan yang sesuai bergantung pada agenda, kebutuhan fleksibilitas, dan ketentuan paket. Tanyakan apakah durasi, biaya sopir, parkir, tol, bahan bakar, dan biaya lain termasuk dalam penawaran atau dihitung terpisah.',
    ],
  },
  {
    number: '03',
    category: 'Panduan Kendaraan',
    title: 'Memilih Mobil untuk Keluarga dan Perjalanan Bisnis',
    paragraphs: [
      'Jumlah penumpang menjadi pertimbangan utama saat menentukan kendaraan. City car dapat dipertimbangkan untuk mobilitas perkotaan dengan kebutuhan ruang yang lebih ringkas, sedangkan MPV dapat menjadi alternatif untuk perjalanan bersama keluarga atau rekan kerja.',
      'Untuk agenda profesional, pertimbangkan kenyamanan perjalanan, ruang barang bawaan, jadwal, serta lokasi pertemuan. Jika membawa beberapa orang sekaligus, pastikan kapasitas kendaraan sesuai dengan jumlah penumpang yang akan ikut.',
      'Untuk rombongan yang lebih besar, tanyakan pilihan kendaraan seperti Hiace dan konfirmasikan kapasitas unit yang tersedia. Pilih kendaraan berdasarkan kebutuhan nyata, bukan hanya kategori atau tampilannya.',
    ],
  },
  {
    number: '04',
    category: 'Panduan Bandara',
    title: 'Tips Memesan Rental Mobil ke Bandara Soekarno-Hatta',
    paragraphs: [
      'Saat memesan transportasi bandara, sampaikan tanggal perjalanan, waktu penerbangan, terminal jika sudah diketahui, jumlah penumpang, dan jumlah bagasi. Informasi ini membantu menentukan waktu penjemputan serta jenis kendaraan yang diperlukan.',
      'Perhitungkan waktu perjalanan dari lokasi awal, kondisi lalu lintas, dan waktu tambahan untuk menghadapi perubahan situasi. Untuk perjalanan penjemputan dari bandara, berikan informasi penerbangan dan titik temu yang jelas kepada penyedia layanan.',
      'Konfirmasikan biaya perjalanan, waktu tunggu, biaya parkir, tol, serta ketentuan perubahan jadwal. Rincian tersebut sebaiknya disepakati sebelum perjalanan agar tidak terjadi kesalahpahaman.',
    ],
  },
  {
    number: '05',
    category: 'Tips Perjalanan',
    title: 'Hal yang Perlu Disiapkan Sebelum Perjalanan Luar Kota',
    paragraphs: [
      'Perjalanan luar kota sebaiknya direncanakan berdasarkan tujuan, perkiraan durasi, jumlah penumpang, dan kebutuhan bagasi. Pastikan kendaraan yang dipilih sesuai dengan rute serta kebutuhan perjalanan.',
      'Tanyakan apakah paket yang ditawarkan mencakup penggunaan dalam kota atau luar kota, batas durasi, bahan bakar, tol, parkir, penginapan sopir jika diperlukan, dan biaya tambahan lain. Jangan berasumsi semua komponen sudah termasuk.',
      'Sebelum keberangkatan, konfirmasikan kembali jadwal, lokasi penjemputan, tujuan, nomor kontak, serta rincian kesepakatan. Persiapan sederhana ini membantu perjalanan berlangsung lebih teratur.',
    ],
  },
  {
    number: '06',
    category: 'Panduan Pemesanan',
    title: 'Informasi yang Perlu Disiapkan Saat Booking Rental Mobil',
    paragraphs: [
      'Agar proses pemesanan lebih mudah, siapkan nama pemesan, nomor kontak aktif, tanggal dan jam penggunaan, kendaraan yang diinginkan, serta jenis layanan. Jika rute sudah diketahui, sampaikan juga titik penjemputan dan tujuan perjalanan.',
      'Tanyakan ketersediaan kendaraan untuk tanggal tersebut, harga paket, durasi sewa, persyaratan, dan biaya tambahan yang mungkin berlaku. Untuk layanan dengan sopir, konfirmasikan pula cakupan penggunaan dan waktu tunggu.',
      'Setelah detail disepakati, simpan konfirmasi pemesanan dan informasi kontak admin. Hindari menganggap pemesanan sudah pasti sebelum ketersediaan serta ketentuan booking dikonfirmasi oleh pihak penyedia.',
    ],
  },
];

const faqs = [
  {
    question: 'Berapa harga sewa mobil Tangerang di NAYVERA?',
    answer:
      'Harga mengikuti jenis kendaraan, tanggal penggunaan, durasi, rute, dan pilihan layanan. Hubungi admin untuk memperoleh penawaran terbaru beserta rincian biaya yang berlaku.',
  },
  {
    question: 'Apakah tersedia sewa mobil Tangerang lepas kunci?',
    answer:
      'Pilihan lepas kunci dapat ditanyakan berdasarkan jenis kendaraan, tanggal sewa, dan persyaratan yang berlaku. Konfirmasikan dokumen serta ketentuan penggunaan sebelum memesan.',
  },
  {
    question: 'Apakah tersedia sewa mobil dengan sopir?',
    answer:
      'Tersedia pilihan layanan dengan sopir sesuai kebutuhan dan ketersediaan. Sampaikan jadwal, rute, dan durasi agar admin dapat menjelaskan paket yang sesuai.',
  },
  {
    question: 'Apakah NAYVERA melayani Tangerang Selatan dan BSD?',
    answer:
      'Kebutuhan penjemputan di Tangerang Selatan, BSD, Serpong, dan kawasan sekitar dapat dikonsultasikan. Konfirmasikan lokasi dan tanggal untuk mengetahui ketersediaan layanan.',
  },
  {
    question: 'Bisakah memesan mobil menuju Bandara Soekarno-Hatta?',
    answer:
      'Anda dapat mengonsultasikan perjalanan menuju atau dari Bandara Soekarno-Hatta. Siapkan jadwal penerbangan, lokasi penjemputan, jumlah penumpang, dan bagasi.',
  },
  {
    question: 'Apakah kendaraan bisa digunakan untuk perjalanan luar kota?',
    answer:
      'Kebutuhan perjalanan luar kota dapat dikonsultasikan terlebih dahulu. Rute, durasi, jenis kendaraan, serta rincian biaya perlu dikonfirmasi sebelum booking.',
  },
  {
    question: 'Bagaimana cara memesan rental mobil Tangerang?',
    answer:
      'Hubungi NAYVERA melalui WhatsApp, sampaikan kendaraan, tanggal, durasi, jenis layanan, lokasi penjemputan, tujuan, dan jumlah penumpang. Lanjutkan setelah ketersediaan dan ketentuan dikonfirmasi.',
  },
  {
    question: 'Apakah harga sudah termasuk tol, parkir, dan bahan bakar?',
    answer:
      'Komponen biaya bergantung pada paket yang ditawarkan. Tanyakan secara langsung apakah tol, parkir, bahan bakar, biaya sopir, dan biaya lainnya termasuk atau dibayar terpisah.',
  },
];

const reasons = [
  {
    icon: Car,
    title: 'Pilihan Kendaraan',
    description:
      'Konsultasikan kategori kendaraan sesuai kebutuhan penumpang, bagasi, dan perjalanan.',
  },
  {
    icon: MessageCircle,
    title: 'Konsultasi Langsung',
    description:
      'Sampaikan kebutuhan melalui WhatsApp untuk menanyakan ketersediaan dan penawaran.',
  },
  {
    icon: MapPin,
    title: 'Rute yang Disesuaikan',
    description:
      'Jelaskan titik jemput dan tujuan agar kebutuhan perjalanan dapat diperiksa sejak awal.',
  },
  {
    icon: ShieldCheck,
    title: 'Informasi Sebelum Booking',
    description:
      'Pastikan paket, persyaratan, durasi, dan rincian biaya dipahami sebelum menyetujui pemesanan.',
  },
];

export default function SewaMobilTangerangPage() {
  const whatsappUrl = createWhatsAppUrl();

  return (
    <main className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-[#07111f]">
        <div className="absolute inset-0 -z-20">
          <img
            src="/assets/images/AYLA DAN AGYA.jpg"
            alt="Pilihan kendaraan rental mobil NAYVERA"
            className="h-full w-full object-cover opacity-30"
          />
        </div>

        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#07111f] via-[#07111f]/95 to-[#07111f]/55" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#07111f] via-transparent to-transparent" />

        <div className="mx-auto grid min-h-[650px] max-w-7xl items-center gap-12 px-6 py-24 lg:grid-cols-[1.15fr_.85fr] lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 px-4 py-2 text-sm font-medium text-[#e4c45a]">
              <MapPin size={16} />
              NAYVERA INDO TRANSPORT
            </div>

            <h1 className="mt-7 text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Sewa Mobil Tangerang
              <span className="mt-2 block text-[#d4af37]">
                Mulai 300 Ribuan
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">
              Rental mobil Tangerang untuk kebutuhan keluarga, bisnis,
              perjalanan bandara, wisata, dan perjalanan luar kota. Konsultasikan
              pilihan kendaraan, layanan lepas kunci atau dengan sopir, serta
              rincian paket sebelum melakukan pemesanan.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-6 py-4 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={19} />
                Konsultasi via WhatsApp
                <ArrowRight size={18} />
              </a>

              <a
                href="#armada"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 py-4 font-semibold text-white transition hover:border-[#d4af37]/60 hover:bg-white/10"
              >
                Lihat Pilihan Armada
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-5 sm:grid-cols-3">
              {[
                ['Pilihan Mobil', 'Berbagai kategori kendaraan'],
                ['Pilihan Layanan', 'Lepas kunci atau sopir'],
                ['Konsultasi', 'Ketersediaan dan paket'],
              ].map(([title, description]) => (
                <div key={title} className="border-l-2 border-[#d4af37] pl-4">
                  <p className="font-bold text-white">{title}</p>
                  <p className="mt-1 text-xs leading-5 text-slate-400">
                    {description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-7 shadow-2xl backdrop-blur">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
                  <Car size={25} />
                </div>
                <div>
                  <p className="font-bold text-white">Butuh kendaraan?</p>
                  <p className="text-sm text-slate-400">
                    Mulai dari kebutuhan perjalanan Anda
                  </p>
                </div>
              </div>

              <div className="mt-7 space-y-4">
                {[
                  'Tentukan tanggal dan durasi',
                  'Pilih kendaraan yang dibutuhkan',
                  'Sampaikan lokasi jemput dan tujuan',
                  'Konfirmasikan harga dan ketentuan',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2
                      size={19}
                      className="mt-0.5 shrink-0 text-[#d4af37]"
                    />
                    <p className="text-sm leading-6 text-slate-300">{item}</p>
                  </div>
                ))}
              </div>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-5 py-3.5 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={18} />
                Tanyakan Ketersediaan
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST / SERVICE SUMMARY */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-6 px-6 py-8 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
          {[
            { icon: Car, title: 'Pilihan Armada', text: 'Sesuaikan dengan kebutuhan' },
            { icon: Users, title: 'Layanan Fleksibel', text: 'Konsultasikan paket sewa' },
            { icon: MapPin, title: 'Area Tangerang', text: 'Konfirmasi lokasi layanan' },
            { icon: MessageCircle, title: 'Booking WhatsApp', text: 'Tanyakan detail langsung' },
          ].map((item) => {
            const Icon = item.icon;

            return (
              <div key={item.title} className="flex items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                  <Icon size={22} />
                </div>
                <div>
                  <p className="font-bold text-[#07111f]">{item.title}</p>
                  <p className="mt-1 text-sm text-slate-500">{item.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[1.1fr_.9fr] lg:px-8">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Rental Mobil Tangerang
            </p>

            <h2 className="mt-3 text-3xl font-extrabold leading-tight text-[#07111f] sm:text-4xl">
              Solusi Transportasi untuk
              <span className="text-[#b89528]"> Berbagai Kebutuhan</span>
            </h2>

            <p className="mt-6 leading-8 text-slate-600">
              NAYVERA INDO TRANSPORT menyediakan pilihan layanan kendaraan
              untuk membantu kebutuhan mobilitas di Tangerang dan sekitarnya.
              Layanan dapat dikonsultasikan untuk aktivitas keluarga,
              perjalanan bisnis, antar jemput bandara, wisata, acara, maupun
              perjalanan antarkota.
            </p>

            <p className="mt-4 leading-8 text-slate-600">
              Sebelum menentukan kendaraan, pertimbangkan jumlah penumpang,
              bagasi, rute, durasi penggunaan, serta pilihan lepas kunci atau
              dengan sopir. Informasi tersebut membantu proses konsultasi
              menjadi lebih terarah.
            </p>

            <p className="mt-4 leading-8 text-slate-600">
              Ketersediaan unit, persyaratan, dan harga dapat berbeda sesuai
              tanggal serta jenis layanan. Hubungi admin untuk mengonfirmasi
              detail sebelum melakukan pemesanan.
            </p>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex items-center gap-2 font-bold text-[#07111f] transition hover:text-[#b89528]"
            >
              Diskusikan kebutuhan perjalanan
              <ArrowRight size={18} />
            </a>
          </div>

          <div className="rounded-3xl bg-[#07111f] p-8 sm:p-10">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
              <ShieldCheck size={28} />
            </div>

            <h3 className="mt-6 text-2xl font-bold text-white">
              Apa yang Perlu Dikonfirmasi?
            </h3>

            <p className="mt-3 leading-7 text-slate-400">
              Pastikan detail utama diketahui sebelum menyetujui pemesanan.
            </p>

            <div className="mt-7 space-y-4">
              {[
                'Ketersediaan kendaraan pada tanggal penggunaan',
                'Harga, durasi, dan cakupan paket sewa',
                'Persyaratan untuk layanan lepas kunci',
                'Ketentuan sopir, tol, parkir, dan bahan bakar',
                'Biaya tambahan dan prosedur pembatalan',
              ].map((item) => (
                <div key={item} className="flex gap-3">
                  <CheckCircle2
                    size={20}
                    className="mt-0.5 shrink-0 text-[#d4af37]"
                  />
                  <span className="text-sm leading-6 text-slate-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#07111f] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Layanan NAYVERA
            </p>

            <h2 className="mt-3 text-3xl font-extrabold text-white sm:text-4xl">
              Rental Mobil Tangerang untuk
              <span className="text-[#d4af37]"> Kebutuhan Anda</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-400">
              Mulai dengan memilih jenis layanan, kemudian konfirmasikan
              ketersediaan, jadwal, dan rincian paket kepada admin.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => {
              const Icon = service.icon;

              return (
                <article
                  key={service.title}
                  className="group rounded-2xl border border-white/10 bg-white/[0.04] p-7 transition duration-300 hover:-translate-y-1 hover:border-[#d4af37]/60 hover:bg-white/[0.07]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#d4af37]/10 text-[#e4c45a] transition group-hover:bg-[#d4af37] group-hover:text-[#07111f]">
                    <Icon size={23} />
                  </div>

                  <h3 className="mt-6 text-xl font-bold text-white">
                    {service.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-400">
                    {service.description}
                  </p>

                  <a
                    href={createWhatsAppUrl(service.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#e4c45a] transition hover:text-white"
                  >
                    Konsultasikan layanan
                    <ArrowRight size={16} />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* NEEDS */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Kebutuhan Perjalanan
            </p>

            <h2 className="mt-3 text-3xl font-extrabold text-[#07111f] sm:text-4xl">
              Pilih Kendaraan Berdasarkan
              <span className="text-[#b89528]"> Agenda Anda</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-600">
              Kebutuhan perjalanan yang berbeda dapat memerlukan jenis
              kendaraan dan paket yang berbeda pula.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map((item) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:border-[#d4af37]/60 hover:shadow-lg"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                    <Icon size={22} />
                  </div>

                  <h3 className="mt-5 text-lg font-bold text-[#07111f]">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    {item.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FLEET */}
      <section id="armada" className="scroll-mt-24 bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-3xl">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
                Pilihan Armada
              </p>

              <h2 className="mt-3 text-3xl font-extrabold text-[#07111f] sm:text-4xl">
                Armada Rental Mobil
                <span className="text-[#b89528]"> Tangerang</span>
              </h2>

              <p className="mt-5 leading-8 text-slate-600">
                Kenali kategori kendaraan berikut sebagai titik awal memilih
                armada. Konfirmasikan unit yang tersedia, kapasitas, serta
                pilihan layanan untuk tanggal penggunaan Anda.
              </p>
            </div>

            <a
              href="/fleet-armada-page"
              className="inline-flex items-center gap-2 font-bold text-[#07111f] transition hover:text-[#b89528]"
            >
              Lihat Armada Lengkap
              <ArrowRight size={18} />
            </a>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {fleet.map((car) => (
              <article
                key={car.name}
                className="group overflow-hidden rounded-3xl border border-slate-200 bg-white transition duration-300 hover:-translate-y-1 hover:border-[#d4af37]/70 hover:shadow-xl"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                  <img
                    src={car.image}
                    alt={`${car.name} untuk rental mobil Tangerang NAYVERA`}
                    loading="lazy"
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />

                  <div className="absolute left-4 top-4 rounded-full bg-[#07111f]/90 px-3 py-1.5 text-xs font-semibold text-[#e4c45a] backdrop-blur">
                    {car.category}
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#07111f]">
                    {car.name}
                  </h3>

                  {/* HARGA SEWA */}
                  <div className="mt-5 border-t border-slate-200 pt-4">
                    <div className="flex items-center justify-between gap-3">
                      {/* INFORMASI HARGA */}
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500">Mulai dari</p>
                        <p className="mt-1 text-xl font-extrabold text-[#07111f]">
                          {car.price}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          12 jam · {car.package}
                        </p>
                      </div>

                      {/* TOMBOL BOOKING */}
                      <div className="flex shrink-0 flex-col gap-2">
                        <a
                          href={createWhatsAppUrl(car.name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-xl bg-[#e5b957] px-5 py-2 text-center text-xs font-bold text-[#07111f] transition hover:bg-[#d4af37]"
                        >
                          Booking
                        </a>
                        <a
                          href={createWhatsAppUrl(car.name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-xl border border-emerald-300 px-5 py-2 text-center text-xs font-bold text-emerald-600 transition hover:bg-emerald-50"
                        >
                          WhatsApp
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* AREA */}
      <section className="bg-[#0b1726] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Area Layanan
            </p>

            <h2 className="mt-3 text-3xl font-extrabold text-white sm:text-4xl">
              Rental Mobil Tangerang
              <span className="text-[#d4af37]"> dan Sekitarnya</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-400">
              Sampaikan alamat penjemputan dan tujuan agar admin dapat
              mengonfirmasi cakupan layanan serta kemungkinan biaya tambahan.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {areaGroups.map((area) => (
              <article
                key={area.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
              >
                <div className="flex items-center gap-2 text-[#e4c45a]">
                  <MapPin size={18} />
                  <h3 className="font-bold text-white">{area.title}</h3>
                </div>

                <p className="mt-4 text-sm leading-6 text-slate-400">
                  {area.description}
                </p>

                <ul className="mt-5 space-y-2.5">
                  {area.places.map((place) => (
                    <li
                      key={place}
                      className="border-b border-white/5 pb-2 text-sm text-slate-300 last:border-0"
                    >
                      {place}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <p className="mt-8 text-sm leading-7 text-slate-400">
            Area yang tercantum merupakan referensi kebutuhan perjalanan.
            Konfirmasikan terlebih dahulu apakah titik penjemputan, tujuan,
            tanggal, dan jenis layanan Anda dapat dilayani.
          </p>
        </div>
      </section>

      {/* AIRPORT */}
      <section className="overflow-hidden bg-[#07111f]">
        <div className="mx-auto grid max-w-7xl lg:grid-cols-2">
          <div className="relative min-h-[380px] bg-slate-900">
            <img
              src="/assets/images/Armada Hiace Nayvera.jpeg"
              alt="Pilihan armada NAYVERA untuk perjalanan bandara dan rombongan"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07111f]/75 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#07111f]" />
          </div>

          <div className="flex items-center px-6 py-16 lg:px-12">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
                Airport Transfer
              </p>

              <h2 className="mt-3 text-3xl font-extrabold leading-tight text-white sm:text-4xl">
                Sewa Mobil Tangerang
                <span className="text-[#d4af37]"> ke Bandara Soekarno-Hatta</span>
              </h2>

              <p className="mt-6 leading-8 text-slate-400">
                Perjalanan bandara membutuhkan koordinasi jadwal dan titik
                jemput yang jelas. Siapkan waktu penerbangan, terminal jika
                diketahui, jumlah penumpang, serta barang bawaan sebelum
                berkonsultasi.
              </p>

              <div className="mt-7 space-y-4">
                {[
                  'Tentukan tanggal dan waktu perjalanan',
                  'Sampaikan lokasi jemput dan terminal tujuan',
                  'Konfirmasikan waktu tunggu dan biaya tambahan',
                  'Pastikan jenis kendaraan sesuai bagasi',
                ].map((item) => (
                  <div key={item} className="flex gap-3">
                    <CheckCircle2
                      size={20}
                      className="mt-0.5 shrink-0 text-[#d4af37]"
                    />
                    <span className="text-sm leading-6 text-slate-300">{item}</span>
                  </div>
                ))}
              </div>

              <a
                href={createWhatsAppUrl('Airport transfer')}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-6 py-3.5 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <Plane size={18} />
                Konsultasi Airport Transfer
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHY NAYVERA */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Panduan Pemesanan
            </p>

            <h2 className="mt-3 text-3xl font-extrabold text-[#07111f] sm:text-4xl">
              Hal yang Perlu Diperiksa
              <span className="text-[#b89528]"> Sebelum Booking</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-600">
              Pemesanan yang jelas dimulai dari kebutuhan perjalanan yang
              terencana. Periksa detail berikut sebelum menyetujui paket sewa.
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {reasons.map((item) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  className="rounded-2xl border border-slate-200 p-6"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                    <Icon size={21} />
                  </div>

                  <h3 className="mt-5 font-bold text-[#07111f]">
                    {item.title}
                  </h3>

                  <p className="mt-2 text-sm leading-7 text-slate-600">
                    {item.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ARTICLES */}
      <section id="panduan" className="scroll-mt-24 bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Pusat Informasi
            </p>

            <h2 className="mt-3 text-3xl font-extrabold text-[#07111f] sm:text-4xl">
              Panduan Rental Mobil
              <span className="text-[#b89528]"> Tangerang</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-600">
              Baca panduan berikut untuk memahami pilihan layanan, menentukan
              kendaraan, dan menyiapkan informasi sebelum memesan.
            </p>
          </div>

          <div className="mt-12 space-y-6">
            {articles.map((article) => (
              <article
                key={article.number}
                className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8"
              >
                <div className="flex flex-col gap-5 sm:flex-row">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#07111f] text-lg font-extrabold text-[#e4c45a]">
                    {article.number}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#b89528]">
                      {article.category}
                    </p>

                    <h3 className="mt-2 text-xl font-bold leading-snug text-[#07111f] sm:text-2xl">
                      {article.title}
                    </h3>

                    <div className="mt-5 space-y-4">
                      {article.paragraphs.map((paragraph, index) => (
                        <p
                          key={`${article.number}-${index}`}
                          className="text-sm leading-7 text-slate-600 sm:text-base"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>

                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#07111f] transition hover:text-[#b89528]"
                    >
                      Tanyakan kebutuhan Anda
                      <ArrowRight size={16} />
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* BOOKING STEPS */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Cara Booking
            </p>

            <h2 className="mt-3 text-3xl font-extrabold text-[#07111f] sm:text-4xl">
              Cara Memesan Rental Mobil
              <span className="text-[#b89528]"> di Tangerang</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {[
              {
                number: '01',
                title: 'Sampaikan Kebutuhan',
                description:
                  'Kirim tanggal, durasi, lokasi penjemputan, tujuan, jumlah penumpang, dan jenis layanan.',
              },
              {
                number: '02',
                title: 'Konfirmasi Kendaraan',
                description:
                  'Tanyakan ketersediaan unit, rincian harga, persyaratan, serta komponen biaya paket.',
              },
              {
                number: '03',
                title: 'Sepakati Pemesanan',
                description:
                  'Periksa detail kesepakatan dan lanjutkan booking setelah informasi dikonfirmasi.',
              },
            ].map((step) => (
              <article
                key={step.number}
                className="rounded-2xl border border-slate-200 bg-white p-7"
              >
                <p className="text-4xl font-black text-[#d4af37]/50">
                  {step.number}
                </p>

                <h3 className="mt-4 text-xl font-bold text-[#07111f]">
                  {step.title}
                </h3>

                <p className="mt-3 text-sm leading-7 text-slate-600">
                  {step.description}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-10 text-center">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#07111f] px-7 py-4 font-bold text-white transition hover:bg-[#d4af37] hover:text-[#07111f]"
            >
              <MessageCircle size={19} />
              Mulai Konsultasi
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="scroll-mt-24 bg-slate-50 py-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              FAQ
            </p>

            <h2 className="mt-3 text-3xl font-extrabold text-[#07111f] sm:text-4xl">
              Pertanyaan Seputar
              <span className="text-[#b89528]"> Rental Mobil Tangerang</span>
            </h2>
          </div>

          <div className="mt-12 space-y-4">
            {faqs.map((faq) => (
              <details
                key={faq.question}
                className="group rounded-2xl border border-slate-200 bg-white"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-5 p-6 font-semibold text-[#07111f]">
                  <span>{faq.question}</span>
                  <ChevronDown
                    size={20}
                    className="shrink-0 text-[#b89528] transition group-open:rotate-180"
                  />
                </summary>

                <div className="border-t border-slate-100 px-6 pb-6 pt-5 text-sm leading-7 text-slate-600">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative isolate overflow-hidden bg-[#07111f] py-20">
        <div className="absolute -right-24 -top-24 -z-10 h-80 w-80 rounded-full bg-[#d4af37]/10 blur-3xl" />
        <div className="absolute -bottom-28 -left-20 -z-10 h-80 w-80 rounded-full bg-[#d4af37]/10 blur-3xl" />

        <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
            <BookOpenText size={27} />
          </div>

          <p className="mt-6 text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
            NAYVERA INDO TRANSPORT
          </p>

          <h2 className="mt-4 text-3xl font-extrabold leading-tight text-white sm:text-5xl">
            Sedang Mencari Rental Mobil
            <span className="block text-[#d4af37]">di Tangerang?</span>
          </h2>

          <p className="mx-auto mt-6 max-w-2xl leading-8 text-slate-400">
            Sampaikan tanggal perjalanan, kendaraan yang dibutuhkan, jenis
            layanan, dan tujuan Anda. Admin NAYVERA dapat membantu memeriksa
            ketersediaan serta menjelaskan rincian paket sebelum pemesanan.
          </p>

          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-9 inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-7 py-4 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
          >
            <MessageCircle size={19} />
            Hubungi NAYVERA
            <ArrowRight size={18} />
          </a>

          <p className="mt-5 text-xs leading-6 text-slate-500">
            Harga, unit, dan ketentuan layanan dikonfirmasi saat konsultasi.
          </p>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </main>
  );
}